import { useEffect, useState } from "react";
import { Mail } from "lucide-react";
import { EmailOps } from "@/lib/api";

const TONE = {
  OK: "border-emerald-400/30 text-emerald-200",
  LOW: "border-amber-400/40 text-amber-200",
  CRITICAL: "border-orange-400/50 text-orange-200",
  EXHAUSTED: "border-rose-400/60 text-rose-200 bg-rose-500/10",
  UNLIMITED: "border-white/15 text-white/70",
};
const LABEL = { LOW: "LOW QUOTA", CRITICAL: "CRITICAL", EXHAUSTED: "QUOTA EXHAUSTED" };

/** Resend daily email quota (provider/configured limit · used · remaining). Polls every 60s. */
export const EmailQuotaChip = ({ compact = false, slot = "sidebar" }) => {
  const [q, setQ] = useState(null);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const load = () => EmailOps.quota().then(setQ).catch(() => {});
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, []);
  if (!q) return null;
  const unlimited = q.limit == null;
  return (
    <div className="relative">
      <button data-testid={`email-quota-chip-${slot}`} data-level={q.level} onClick={() => setOpen((o) => !o)} title="Resend daily email quota"
        className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 min-h-[30px] text-[10px] font-semibold tracking-wide hover:bg-white/[0.05] transition-colors ${TONE[q.level] || TONE.OK}`}>
        <Mail size={12} />
        {compact ? <span data-testid={`email-quota-used-${slot}`}>{q.used}</span> : (
          <span data-testid={`email-quota-used-${slot}`}>Emails {q.used}{unlimited ? "" : ` / ${q.limit}`}</span>
        )}
        {LABEL[q.level] && !compact && <span className="ml-1 px-1.5 rounded bg-white/10 text-[8px] uppercase">{LABEL[q.level]}</span>}
      </button>
      {open && (
        <div data-testid={`email-quota-panel-${slot}`} className="absolute z-[70] top-9 left-0 w-[260px] rounded-xl border border-[#D4AF37]/30 bg-[#04102B] shadow-2xl p-3 text-[11px] text-white/80 space-y-1.5">
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#D4AF37]">Resend daily quota</div>
          <div className="flex justify-between"><span>Provider / configured limit</span><strong data-testid="email-quota-limit">{unlimited ? "No limit" : q.limit}</strong></div>
          <div className="flex justify-between"><span>Used today</span><strong>{q.used}</strong></div>
          <div className="flex justify-between"><span>Remaining</span><strong data-testid="email-quota-remaining">{unlimited ? "∞" : q.remaining}</strong></div>
          {!unlimited && (
            <div className="h-1.5 rounded-full bg-white/10 overflow-hidden"><div className={`h-full ${q.level === "OK" ? "bg-emerald-400" : q.level === "LOW" ? "bg-amber-400" : "bg-rose-500"}`} style={{ width: `${q.percent}%` }} /></div>
          )}
          <div className="text-white/55">Resets {q.resets_at_ist}</div>
          <div className="text-white/45">Configured via <code>{q.configured_via}</code> · counted per UTC day</div>
          {q.level === "EXHAUSTED" && <div className="text-rose-200 font-semibold">New sends are blocked (EMAIL_QUOTA_EXHAUSTED).</div>}
        </div>
      )}
    </div>
  );
};
